import { useNats } from '@khal-os/sdk/app';
import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { AppEnvClient, AppEnvManifestEntry } from '../../lib/app-env/types';
import type { AppSettingsTab } from '../../stores/app-settings-store';
import { AppEnvPanel } from './AppEnvPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Manifest info passed from the consumer (via WindowRenderer). */
export interface AppManifestInfo {
	label: string;
	permission?: string;
	minRole?: string;
	natsPrefix?: string;
	runtimeKind?: string;
	launchUrl?: string;
	storeMeta?: {
		name: string;
		version: string;
		author: string;
		description: string;
		permissions?: string[];
	};
	/** Manifest-declared env schema. When present + non-empty, the
	 *  Environment tab renders. Admins always see the tab. */
	env?: AppEnvManifestEntry[];
}

interface AppSettingsPanelProps {
	windowId: string;
	appId: string;
	manifest?: AppManifestInfo;
	onClose: () => void;
	/** Tab to focus when the panel mounts. Defaults to 'about'. */
	initialTab?: AppSettingsTab;
	/** Required to render the Environment tab. When omitted, the tab is
	 *  hidden even if the manifest declares env. */
	envClient?: AppEnvClient;
	/** `installed_apps.id` — required when `envClient` is supplied so the
	 *  client can target the right install. */
	installedAppId?: string;
	/** Whether the current user has admin role — gates the Environment tab
	 *  visibility (admins always see it) and export. */
	isAdmin?: boolean;
}

interface ServiceInfo {
	name: string;
	running: boolean;
	pid: number | null;
	source: string;
	ports: number[];
	proxyPorts: Array<{ internalPort: number; proxyPort: number }>;
	restartPolicy: string;
	circuitBroken: boolean;
	uptime?: number;
}

interface AppVersionInfo {
	version?: string;
	installDate?: string;
}

interface RuntimeV2Snapshot {
	state?: string;
	status?: string;
	runtimeKind?: string;
	image?: string;
	url?: string;
	ports?: number[];
	updatedAt?: string;
	startedAt?: string;
	uptimeMs?: number;
	logs?: string[];
	logsSource?: string;
	kubernetes?: {
		namespace?: string;
		deployment?: string;
		service?: string | null;
		pods?: Array<{
			name?: string;
			phase?: string;
			ready?: boolean;
			restarts?: number;
			node?: string;
			image?: string;
			imageID?: string;
			resources?: {
				requests?: Record<string, string | undefined>;
				limits?: Record<string, string | undefined>;
			};
		}>;
	};
	health?: {
		url?: string;
		path?: string;
		status?: string;
		httpStatus?: number;
		latencyMs?: number;
		checkedAt?: string;
		reason?: string;
	};
	cost?: {
		source?: string;
		currency?: string;
		estimatedHourly?: number;
		estimatedDaily?: number;
		series?: Array<{ label: string; value: number }>;
		notes?: string[];
	};
}

interface RuntimeV2State {
	loading: boolean;
	snapshot?: RuntimeV2Snapshot;
	runtimeApiWired: boolean;
	logsWired: boolean;
	error?: string;
}

type ProviderHealthStatus = 'healthy' | 'degraded' | 'down' | 'missing' | 'unavailable' | 'not-wired';

interface ProviderHealthRow {
	name: string;
	status: ProviderHealthStatus;
	message?: string;
	configured?: boolean;
	latencyMs?: number;
	count?: number;
	contentType?: string;
}

interface ProviderHealthState {
	loading: boolean;
	wired: boolean;
	rows: ProviderHealthRow[];
	label: string;
}

// ---------------------------------------------------------------------------
// Tickets v2 env connector grouping
// ---------------------------------------------------------------------------

type EnvGroupKey = 'khal-platform' | 'elevenlabs' | 'omni-whatsapp' | 'recordings-transcripts';

const TICKETS_V2_ENV_GROUPS: Record<string, EnvGroupKey> = {
	KHAL_NATS_URL: 'khal-platform',
	KHAL_SESSION_SECRET: 'khal-platform',
	KHAL_ORG_ID: 'khal-platform',
	ELEVENLABS_API_KEY: 'elevenlabs',
	EL_API_KEY: 'elevenlabs',
	ELEVENLABS_BASE_URL: 'elevenlabs',
	OMNI_BASE_URL: 'omni-whatsapp',
	OMNI_URL: 'omni-whatsapp',
	OMNI_API_KEY: 'omni-whatsapp',
	WHATSAPP_HISTORY_SOURCE: 'omni-whatsapp',
	OUTBOUND_CALL_RECORDS_URL: 'recordings-transcripts',
	TRANSCRIPT_STORAGE_URL: 'recordings-transcripts',
};

const ENV_GROUP_LABELS: Record<EnvGroupKey, string> = {
	'khal-platform': 'KHAL platform',
	elevenlabs: 'ElevenLabs voice history',
	'omni-whatsapp': 'Omni / WhatsApp history',
	'recordings-transcripts': 'Recordings and transcripts',
};

function resolveEnvGrouping(key: string): string | undefined {
	const groupKey = TICKETS_V2_ENV_GROUPS[key];
	if (!groupKey) return undefined;
	return ENV_GROUP_LABELS[groupKey];
}

const PROVIDER_LABELS: Record<string, string> = {
	elevenlabs: 'ElevenLabs history',
	elevenlabsAudio: 'ElevenLabs audio',
	omni: 'Omni WhatsApp/text history',
	outbound: 'Outbound call records',
	authBridge: 'KHAL session/auth bridge',
};

function friendlyProviderLabel(value: string): string {
	return PROVIDER_LABELS[value] ?? value;
}

const TOKEN_REDACTION_PATTERNS: RegExp[] = [
	/(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi,
	/((?:api[_-]?key|token|secret|password|authorization)\s*[:=]\s*)[^\s,;]+/gi,
	/([?&](?:token|api_key|apikey|key|secret)=)[^\s&]+/gi,
];

function redactLogLine(line: string): string {
	return TOKEN_REDACTION_PATTERNS.reduce((value, pattern) => value.replace(pattern, '$1[REDACTED]'), line).slice(0, 1000);
}

function safeDisplayText(value: unknown): string {
	return redactLogLine(String(value ?? 'not wired')).replace(/https?:\/\/[^\s)]+/gi, '[redacted-url]');
}

function sanitizeLogLines(lines: string[], cap = 50): string[] {
	return lines.slice(-cap).map((line) => redactLogLine(String(line)));
}

function parseProviderRows(payload: unknown): ProviderHealthRow[] {
	const data = payload as { providers?: unknown; checks?: unknown } | ProviderHealthRow[] | undefined;
	const source = Array.isArray(data) ? data : (data?.providers ?? data?.checks);
	if (Array.isArray(source)) {
		return source.map((row, index) => {
			const r = row as { name?: string; id?: string; provider?: string; status?: unknown; ready?: boolean; configured?: boolean; message?: string; reason?: string; error?: string; latencyMs?: number; count?: number; contentType?: string };
			return {
				name: friendlyProviderLabel(r.name ?? r.provider ?? r.id ?? `Provider ${index + 1}`),
				status: r.ready === true ? 'healthy' : normalizeProviderStatus(r.status),
				message: r.message ?? r.reason ?? r.error,
				configured: r.configured,
				latencyMs: r.latencyMs,
				count: r.count,
				contentType: r.contentType,
			};
		});
	}
	if (source && typeof source === 'object') {
		return Object.entries(source as Record<string, unknown>).map(([name, value]) => {
			const v = value as { status?: unknown; ready?: boolean; configured?: boolean; message?: string; reason?: string; error?: string; latencyMs?: number; count?: number; contentType?: string };
			return {
				name: friendlyProviderLabel(name),
				status: typeof value === 'boolean' ? (value ? 'healthy' : 'missing') : v.ready === true ? 'healthy' : normalizeProviderStatus(v.status ?? value),
				message: typeof value === 'object' && value ? (v.message ?? v.reason ?? v.error) : undefined,
				configured: typeof value === 'object' && value ? v.configured : undefined,
				latencyMs: typeof value === 'object' && value ? v.latencyMs : undefined,
				count: typeof value === 'object' && value ? v.count : undefined,
				contentType: typeof value === 'object' && value ? v.contentType : undefined,
			};
		});
	}
	return [];
}

function useRuntimeV2Snapshot(appId: string, installedAppId?: string, enabled = false, canReadLogs = false): RuntimeV2State {
	const { connected, request } = useNats();
	const [state, setState] = useState<RuntimeV2State>({ loading: enabled, runtimeApiWired: false, logsWired: false });

	useEffect(() => {
		let cancelled = false;
		if (!enabled) {
			setState({ loading: false, runtimeApiWired: false, logsWired: false });
			return;
		}
		if (!connected) {
			setState({ loading: false, runtimeApiWired: false, logsWired: false, error: 'NATS runtime bridge not connected' });
			return;
		}

		setState((prev) => ({ ...prev, loading: true }));
		(async () => {
			let snapshot: RuntimeV2Snapshot | undefined;
			let runtimeApiWired = false;
			let logsWired = false;
			let error: string | undefined;
			try {
				const reply = await request('os.app.runtime.get', { appId, installedAppId: installedAppId ?? appId });
				snapshot = (reply as { runtime?: RuntimeV2Snapshot; snapshot?: RuntimeV2Snapshot })?.runtime ?? (reply as { snapshot?: RuntimeV2Snapshot })?.snapshot ?? (reply as RuntimeV2Snapshot);
				runtimeApiWired = true;
			} catch (err) {
				error = err instanceof Error ? err.message : 'Runtime API not wired';
			}
			if (canReadLogs) try {
				const reply = await request('os.app.runtime.logs', { appId, installedAppId: installedAppId ?? appId, lines: 80 });
				const lines = (reply as { lines?: string[]; logs?: string[] })?.lines ?? (reply as { logs?: string[] })?.logs;
				if (Array.isArray(lines)) {
					snapshot = { ...(snapshot ?? {}), logs: sanitizeLogLines(lines) };
					logsWired = true;
				}
			} catch {
				logsWired = false;
			}
			if (!cancelled) setState({ loading: false, snapshot, runtimeApiWired, logsWired, error });
		})();

		return () => {
			cancelled = true;
		};
	}, [appId, canReadLogs, connected, enabled, installedAppId, request]);

	return state;
}

function useProviderHealth(appId: string, manifest?: AppManifestInfo): ProviderHealthState {
	const providerHealthUrl = (() => {
		if (!manifest?.launchUrl) return '/api/tickets/health/providers';
		try {
			return new URL('/api/tickets/health/providers', manifest.launchUrl).toString();
		} catch {
			return '/api/tickets/health/providers';
		}
	})();
	const isTicketsProviderApp = appId === 'tickets' || appId === 'tickets-v2' || /tickets/i.test(manifest?.label ?? '');
	const canProbe = isTicketsProviderApp;
	const [state, setState] = useState<ProviderHealthState>({
		loading: canProbe,
		wired: false,
		rows: [],
		label: canProbe ? 'Checking provider health endpoint…' : 'Provider health BFF not wired for this app',
	});

	useEffect(() => {
		let cancelled = false;
		if (!canProbe || typeof fetch !== 'function') {
			setState({ loading: false, wired: false, rows: [], label: canProbe ? 'Provider health BFF not wired: fetch unavailable' : 'Provider health BFF not wired for this app' });
			return;
		}

		setState({ loading: true, wired: false, rows: [], label: 'Checking provider health endpoint…' });
		const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
		const timeout = setTimeout(() => controller?.abort(), 5000);
		fetch(providerHealthUrl, { credentials: 'include', headers: { Accept: 'application/json' }, signal: controller?.signal })
			.then(async (response) => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json() as Promise<unknown>;
			})
			.then((payload) => {
				clearTimeout(timeout);
				if (cancelled) return;
				const rows = parseProviderRows(payload);
				setState({
					loading: false,
					wired: rows.length > 0,
					rows,
					label: rows.length > 0 ? 'Provider health reported by app BFF' : 'Provider health BFF returned no provider rows',
				});
			})
			.catch(() => {
				clearTimeout(timeout);
				if (cancelled) return;
				setState({ loading: false, wired: false, rows: [], label: 'Provider health BFF unavailable or not authenticated' });
			});

		return () => {
			cancelled = true;
			clearTimeout(timeout);
		};
	}, [canProbe, providerHealthUrl]);

	return state;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const COPPER = 'var(--khal-accent)';
const COPPER_SOFT = 'color-mix(in oklch, var(--khal-accent) 12%, transparent)';
const COPPER_TEXT = 'color-mix(in oklch, var(--khal-fg) 72%, var(--khal-accent))';

function formatUptime(ms: number): string {
	const seconds = Math.floor(ms / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	if (days > 0) return `${days}d ${hours % 24}h`;
	if (hours > 0) return `${hours}h ${minutes % 60}m`;
	if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
	return `${seconds}s`;
}

function formatMoney(value: number | undefined, currency = 'USD'): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 'not wired';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		maximumFractionDigits: value < 1 ? 4 : 2,
	}).format(value);
}

/** Check if an app exposes runtime status information. */
function appHasServices(_manifest?: AppManifestInfo): boolean {
	// Always show Status tab — runtime health is universally useful,
	// even for runtime-v2 apps that declare env but omit natsPrefix.
	return true;
}

function isRuntimeV2Image(appId: string, manifest?: AppManifestInfo): boolean {
	const runtimeKind = manifest?.runtimeKind?.toLowerCase();
	return runtimeKind === 'v2-image' || runtimeKind === 'v2-iframe' || Boolean(manifest?.launchUrl) || appId === 'tickets-v2';
}

function runtimeLabel(manifest?: AppManifestInfo): string {
	if (manifest?.runtimeKind) return manifest.runtimeKind;
	if (manifest?.launchUrl) return 'v2-image';
	return manifest?.natsPrefix ? 'Service' : 'Embedded';
}

function normalizeProviderStatus(value: unknown): ProviderHealthStatus {
	const normalized = String(value ?? '').toLowerCase();
	if (['ok', 'ready', 'healthy', 'up', 'connected', 'configured'].includes(normalized)) return 'healthy';
	if (['warn', 'warning', 'degraded', 'partial'].includes(normalized)) return 'degraded';
	if (['down', 'error', 'failed', 'unhealthy'].includes(normalized)) return 'down';
	if (['missing', 'unconfigured', 'not-configured'].includes(normalized)) return 'missing';
	if (normalized === 'unavailable') return 'unavailable';
	return 'not-wired';
}

function providerBadgeVariant(status: ProviderHealthStatus): 'running' | 'needs-config' | 'unavailable' | 'missing' {
	if (status === 'healthy') return 'running';
	if (status === 'degraded') return 'needs-config';
	if (status === 'missing') return 'missing';
	return 'unavailable';
}

function providerStatusLabel(status: ProviderHealthStatus): string {
	if (status === 'healthy') return 'Healthy';
	if (status === 'degraded') return 'Degraded';
	if (status === 'down') return 'Down';
	if (status === 'missing') return 'Missing config';
	if (status === 'unavailable') return 'Unavailable';
	return 'Not wired';
}

/** Environment tab visibility: declared schema OR admin override. */
function appHasEnvTab(
	manifest: AppManifestInfo | undefined,
	envClient: AppEnvClient | undefined,
	isAdmin: boolean,
): boolean {
	if (!envClient) return false;
	const declared = manifest?.env ?? [];
	return declared.length > 0 || isAdmin;
}

// ---------------------------------------------------------------------------
// Visual design tokens (copper-first KHAL)
// ---------------------------------------------------------------------------

const surfaceRaised = 'var(--khal-bg-elevated, var(--khal-surface))';
const borderDefault = 'var(--khal-border)';
const textPrimary = 'var(--khal-fg)';
const textMuted = 'var(--khal-muted)';
const surfaceDefault = 'var(--khal-surface)';
const statusLive = 'var(--khal-success)';
const statusError = 'var(--khal-error)';
const controlsBg = 'var(--khal-cell)';

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function StatusBadge({
	variant,
	children,
}: {
	variant: 'running' | 'needs-config' | 'unavailable' | 'configured' | 'missing' | 'optional';
	children: React.ReactNode;
}) {
	return (
		<span
			className={`k-badge ${
				variant === 'running' || variant === 'configured'
					? 'k-badge-success'
					: variant === 'needs-config'
						? 'k-badge-warning'
						: variant === 'missing'
							? 'k-badge-danger'
							: 'k-badge-muted'
			}`}
			style={{ fontSize: 11 }}
		>
			<span className="k-dot" />
			{children}
		</span>
	);
}

function SettingsCard({
	children,
	padded = true,
}: {
	children: React.ReactNode;
	padded?: boolean;
}) {
	return (
		<div
			className="k-card"
			style={{
				borderColor: borderDefault,
				background: surfaceRaised,
				padding: padded ? 16 : 0,
			}}
		>
			{children}
		</div>
	);
}

function SettingsSectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
	return (
		<div style={{ marginBottom: 12 }}>
			<div style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>{title}</div>
			{subtitle && <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{subtitle}</div>}
		</div>
	);
}

function MetadataRow({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
	return (
		<div className="flex items-center justify-between" style={{ padding: '6px 0' }}>
			<span style={{ fontSize: 12, color: textMuted }}>{label}</span>
			<span
				style={{
					fontSize: 12,
					color: textPrimary,
					fontWeight: 500,
					fontFamily: mono ? "'Geist Mono', monospace" : undefined,
				}}
			>
				{children}
			</span>
		</div>
	);
}

function MissingDataNotice({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				fontSize: 12,
				color: textMuted,
				lineHeight: 1.5,
				border: `1px solid ${borderDefault}`,
				borderRadius: 'var(--khal-radius-sm, 8px)',
				borderColor: borderDefault,
				background: 'color-mix(in oklch, var(--khal-surface) 80%, transparent)',
				padding: 12,
			}}
		>
			{children}
		</div>
	);
}

function EnvironmentUnavailableTab() {
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<SettingsCard>
				<SettingsSectionHeader
					title="Environment"
					subtitle="Secrets stay masked; runtime reconciliation requires the app config client."
				/>
				<MissingDataNotice>
					Environment data source not wired: AppEnvClient unavailable for os.app.config.* subjects.
				</MissingDataNotice>
			</SettingsCard>
		</div>
	);
}

// ---------------------------------------------------------------------------
// About Tab
// ---------------------------------------------------------------------------

function AboutTab({ appId, manifest }: { appId: string; manifest?: AppManifestInfo }) {
	const { connected, request, orgId } = useNats();
	const [versionInfo, setVersionInfo] = useState<AppVersionInfo>({});

	useEffect(() => {
		if (!connected) return;
		request('os.apps.get', { appId })
			.then((reply) => {
				const data = reply as { version?: string; installed_at?: string };
				setVersionInfo({ version: data.version, installDate: data.installed_at });
			})
			.catch(() => {});
	}, [connected, request, orgId, appId]);

	const isTickets = appId === 'tickets-v2' || appId === 'tickets';

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Summary card */}
			<SettingsCard>
				<div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
					<div
						className="flex items-center justify-center rounded-xl"
						style={{
							width: 48,
							height: 48,
							background: COPPER_SOFT,
							color: COPPER_TEXT,
							fontSize: 20,
							fontWeight: 700,
							flexShrink: 0,
						}}
					>
						{(manifest?.label ?? appId).charAt(0).toUpperCase()}
					</div>
					<div>
						<div style={{ fontSize: 16, fontWeight: 600, color: textPrimary }}>
							{manifest?.label ?? appId}
						</div>
						<div style={{ fontSize: 12, color: textMuted, fontFamily: "'Geist Mono', monospace" }}>
							{appId}
						</div>
						{manifest?.storeMeta?.description && (
							<div style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>
								{manifest.storeMeta.description}
							</div>
						)}
					</div>
				</div>
			</SettingsCard>

			{/* Details grid */}
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
				<SettingsCard>
					<SettingsSectionHeader title="Identity" />
					<MetadataRow label="App id" mono>{appId}</MetadataRow>
					{versionInfo.version && <MetadataRow label="Version" mono>{versionInfo.version}</MetadataRow>}
					{versionInfo.installDate && (
						<MetadataRow label="Installed">{new Date(versionInfo.installDate).toLocaleDateString()}</MetadataRow>
					)}
					<MetadataRow label="Runtime">{runtimeLabel(manifest)}</MetadataRow>
				</SettingsCard>

				<SettingsCard>
					<SettingsSectionHeader title="Access" />
					<MetadataRow label="Permission">{manifest?.permission ?? '—'}</MetadataRow>
					<MetadataRow label="Min role">{manifest?.minRole ?? '—'}</MetadataRow>
					{manifest?.natsPrefix && <MetadataRow label="NATS prefix" mono>{manifest.natsPrefix}</MetadataRow>}
					{manifest?.storeMeta?.permissions && manifest.storeMeta.permissions.length > 0 && (
						<MetadataRow label="Resources">{manifest.storeMeta.permissions.join(', ')}</MetadataRow>
					)}
				</SettingsCard>
			</div>

			{/* Scope note for Tickets v2 */}
			{isTickets && (
				<SettingsCard>
					<SettingsSectionHeader title="Launch scope" />
					<div style={{ fontSize: 12, color: textMuted, lineHeight: 1.6 }}>
						Authenticated, embedded, read-only customer-service call/chat history viewer.
						Create, assign, close, and destructive ticket mutations are out of scope for this launch.
					</div>
				</SettingsCard>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Runtime Tab
// ---------------------------------------------------------------------------

function RuntimeTab({ appId, manifest, installedAppId, isAdmin }: { appId: string; manifest?: AppManifestInfo; installedAppId?: string; isAdmin: boolean }) {
	const isV2 = isRuntimeV2Image(appId, manifest);
	const { connected, request, subscribe, orgId } = useNats();
	const [services, setServices] = useState<ServiceInfo[]>([]);
	const [loading, setLoading] = useState(!isV2);
	const runtime = useRuntimeV2Snapshot(appId, installedAppId, isV2, false);

	useEffect(() => {
		if (isV2) {
			setLoading(false);
			setServices([]);
			return;
		}
		if (!connected) {
			setLoading(false);
			return;
		}
		const filterServices = (all: ServiceInfo[]) => {
			const prefix = manifest?.natsPrefix;
			const filtered = prefix
				? all.filter((s) => s.name.includes(prefix) || s.source?.includes(appId))
				: all.filter((s) => s.source?.includes(appId));
			return filtered.length > 0 ? filtered : all;
		};
		const fetchServices = async () => {
			try {
				const reply = await request(`khal.${orgId}.services.list`, {});
				setServices(filterServices((reply as { services?: ServiceInfo[] }).services ?? []));
			} catch {
				setServices([]);
			} finally {
				setLoading(false);
			}
		};
		fetchServices();

		const unsub = subscribe('khal._internal.services.changed', (data) => {
			const payload = data as { services?: ServiceInfo[] };
			if (payload.services) {
				setServices(filterServices(payload.services));
				setLoading(false);
			}
		});
		return unsub;
	}, [connected, isV2, request, subscribe, orgId, appId, manifest?.natsPrefix]);

	if (loading || runtime.loading) {
		return <div style={{ fontSize: 13, color: textMuted, padding: '16px 0' }}>Loading runtime status…</div>;
	}

	const hasServices = services.length > 0;
	const runtimeState = runtime.snapshot?.state ?? runtime.snapshot?.status;
	const runtimeReady = isV2 ? Boolean(runtime.runtimeApiWired && runtimeState && !['stopped', 'error', 'failed'].includes(runtimeState.toLowerCase())) : hasServices;
	const readinessText = isV2
		? runtime.runtimeApiWired
			? `Runtime API wired${runtimeState ? ` · ${runtimeState}` : ''}`
			: 'Runtime API not wired: os.app.runtime.get unavailable'
		: hasServices
			? `${services.filter((s) => s.running).length}/${services.length} services running`
			: 'Legacy service runtime not detected';
	const installedAppDisplay = installedAppId ?? 'Missing installed app id (not wired)';

	return (
		<div className="k-content" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<SettingsCard>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					<StatusBadge variant={runtimeReady ? 'running' : isV2 ? 'unavailable' : 'needs-config'}>
						{runtimeReady ? 'Ready' : isV2 ? 'Runtime API not wired' : 'Needs configuration'}
					</StatusBadge>
					<span style={{ fontSize: 12, color: textMuted }}>{readinessText}</span>
				</div>
			</SettingsCard>

			{isV2 ? (
				<SettingsCard>
					<SettingsSectionHeader title="Runtime-OCI snapshot" subtitle="Uses os.app.runtime.get; missing fields name their upstream source." />
					<div className="k-chart" style={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: 14, alignItems: 'center' }}>
						<div className="k-donut" aria-label="Runtime API availability" style={{ ['--p' as string]: runtime.runtimeApiWired ? '100' : '0', background: `conic-gradient(var(--khal-accent) ${runtime.runtimeApiWired ? '100%' : '0%'}, var(--khal-border) 0)` }}>
							<div className="k-donut__center"><b>{runtime.runtimeApiWired ? 'API' : '—'}</b><span>NATS</span></div>
						</div>
						<div>
							<MetadataRow label="App id" mono>{appId}</MetadataRow>
							<MetadataRow label="Installed app id" mono>{installedAppDisplay}</MetadataRow>
							<MetadataRow label="Runtime kind">{runtime.snapshot?.runtimeKind ?? runtimeLabel(manifest)}</MetadataRow>
							{manifest?.launchUrl && <MetadataRow label="Launch URL">configured ([redacted-url])</MetadataRow>}
							{runtime.snapshot?.image && <MetadataRow label="Image" mono>{runtime.snapshot.image}</MetadataRow>}
							{runtime.snapshot?.ports && runtime.snapshot.ports.length > 0 && <MetadataRow label="Ports" mono>{runtime.snapshot.ports.join(', ')}</MetadataRow>}
							{runtime.snapshot?.uptimeMs != null && <MetadataRow label="Uptime">{formatUptime(runtime.snapshot.uptimeMs)}</MetadataRow>}
						</div>
					</div>
					<div style={{ fontSize: 12, color: textMuted, marginTop: 10 }}>
						{runtime.runtimeApiWired ? 'Runtime snapshot loaded from NATS.' : `Runtime API not wired: ${runtime.error ?? 'os.app.runtime.get unavailable'}`}
					</div>
					{runtime.snapshot?.kubernetes ? (
						<div style={{ marginTop: 12 }}>
							<MetadataRow label="Namespace" mono>{runtime.snapshot.kubernetes.namespace ?? 'not wired'}</MetadataRow>
							<MetadataRow label="Deployment" mono>{runtime.snapshot.kubernetes.deployment ?? 'not wired'}</MetadataRow>
							<MetadataRow label="Service" mono>{runtime.snapshot.kubernetes.service ?? 'not wired'}</MetadataRow>
							{runtime.snapshot.kubernetes.pods?.map((pod) => (
								<div key={pod.name ?? 'pod'} style={{ marginTop: 8, padding: 10, border: `1px solid ${borderDefault}`, borderRadius: 'var(--khal-radius-sm, 8px)' }}>
									<MetadataRow label="Pod" mono>{pod.name ?? 'not wired'}</MetadataRow>
									<MetadataRow label="Phase">{pod.phase ?? 'not wired'}</MetadataRow>
									<MetadataRow label="Ready">{pod.ready == null ? 'not wired' : pod.ready ? 'yes' : 'no'}</MetadataRow>
									<MetadataRow label="Restarts">{pod.restarts ?? 'not wired'}</MetadataRow>
									{pod.node && <MetadataRow label="Node" mono>{pod.node}</MetadataRow>}
									{pod.image && <MetadataRow label="Pod image" mono>{pod.image}</MetadataRow>}
								</div>
							))}
						</div>
					) : (
						<MissingDataNotice>
							Deployment, pod, service, and OCI image data source not wired: os.app.runtime.get returned no kubernetes snapshot.
						</MissingDataNotice>
					)}
					{runtime.snapshot?.health ? (
						<div style={{ marginTop: 12 }}>
							<MetadataRow label="Health URL">{runtime.snapshot.health.url ? 'configured ([redacted-url])' : 'not wired'}</MetadataRow>
							<MetadataRow label="Health status">{safeDisplayText(runtime.snapshot.health.status ?? 'not wired')}</MetadataRow>
							{runtime.snapshot.health.httpStatus && <MetadataRow label="HTTP">{runtime.snapshot.health.httpStatus}</MetadataRow>}
							{runtime.snapshot.health.latencyMs != null && <MetadataRow label="Latency">{runtime.snapshot.health.latencyMs}ms</MetadataRow>}
							{runtime.snapshot.health.reason && <MetadataRow label="Reason">{safeDisplayText(runtime.snapshot.health.reason)}</MetadataRow>}
						</div>
					) : (
						<MissingDataNotice>
							Health probe data source not wired: os.app.runtime.get returned no health snapshot.
						</MissingDataNotice>
					)}
				</SettingsCard>
			) : services.length > 0 ? (
				services.map((svc) => (
					<SettingsCard key={svc.name}>
						<div className="k-row" style={{ gridTemplateColumns: '16px 1fr auto', padding: 0, borderBottom: 'none' }}>
							<span className="rounded-full" style={{ width: 8, height: 8, background: svc.running ? statusLive : statusError }} />
							<span className="k-row-name">{svc.name}</span>
							<StatusBadge variant={svc.running ? 'running' : 'unavailable'}>{svc.running ? 'Running' : 'Stopped'}</StatusBadge>
						</div>
						<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
							{svc.pid && <span style={{ fontSize: 11, color: textMuted, fontFamily: "'Geist Mono', monospace" }}>PID {svc.pid}</span>}
							{svc.uptime != null && <span style={{ fontSize: 11, color: textMuted }}>{formatUptime(svc.uptime)}</span>}
							{svc.ports?.length > 0 && <span style={{ fontSize: 11, color: textMuted, fontFamily: "'Geist Mono', monospace" }}>ports: {svc.ports.join(', ')}</span>}
							{svc.restartPolicy && <span style={{ fontSize: 11, color: textMuted }}>restart: {svc.restartPolicy}</span>}
							{svc.circuitBroken && <span style={{ fontSize: 11, color: statusError }}>circuit broken</span>}
						</div>
						{svc.running && <LogViewer serviceName={svc.name} isAdmin={isAdmin} />}
					</SettingsCard>
				))
			) : (
				<SettingsCard>
					<div style={{ fontSize: 13, color: textMuted, padding: '8px 0' }}>Legacy service runtime not detected for this app.</div>
				</SettingsCard>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Provider, Logs, and Usage Tabs
// ---------------------------------------------------------------------------

function ProvidersTab({ appId, manifest }: { appId: string; manifest?: AppManifestInfo }) {
	const providers = useProviderHealth(appId, manifest);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<SettingsCard>
				<SettingsSectionHeader title="Providers" subtitle={providers.label} />
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					{providers.loading && <div style={{ fontSize: 12, color: textMuted }}>Checking provider health…</div>}
					{!providers.loading && providers.rows.length === 0 && (
						<div className="flex items-center justify-between" style={{ padding: '4px 0' }}>
							<span style={{ fontSize: 12, color: textPrimary }}>App provider health endpoint</span>
							<StatusBadge variant="unavailable">Not wired</StatusBadge>
						</div>
					)}
					{providers.rows.map((p) => {
						const details = [
							p.configured === false ? 'not configured' : p.configured === true ? 'configured' : undefined,
							typeof p.latencyMs === 'number' ? `${Math.round(p.latencyMs)}ms` : undefined,
							typeof p.count === 'number' ? `${p.count} sample${p.count === 1 ? '' : 's'}` : undefined,
							p.contentType ? `content-type ${p.contentType}` : undefined,
						].filter(Boolean).join(' · ');
						return (
							<div key={p.name} className="flex items-center justify-between" style={{ padding: '4px 0', gap: 12 }}>
								<span style={{ fontSize: 12, color: textPrimary }}>
									{p.name}
									{details ? <span style={{ color: textMuted }}> · {details}</span> : null}
									{p.message ? <span style={{ color: textMuted }}> · {safeDisplayText(p.message)}</span> : null}
								</span>
								<StatusBadge variant={providerBadgeVariant(p.status)}>{providerStatusLabel(p.status)}</StatusBadge>
							</div>
						);
					})}
				</div>
			</SettingsCard>
		</div>
	);
}

function LogsTab({ appId, manifest, installedAppId, isAdmin }: { appId: string; manifest?: AppManifestInfo; installedAppId?: string; isAdmin: boolean }) {
	const isV2 = isRuntimeV2Image(appId, manifest);
	const runtime = useRuntimeV2Snapshot(appId, installedAppId, isV2, isAdmin);

	if (!isV2) {
		return (
			<SettingsCard>
				<SettingsSectionHeader title="Logs" subtitle="Legacy service logs stay attached to each service row in the Runtime tab." />
				<MissingDataNotice>
					Legacy log source is khal.&lt;org&gt;.services.logs.&lt;service&gt;; select a running service in Runtime to stream it.
				</MissingDataNotice>
			</SettingsCard>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<SettingsCard>
				<SettingsSectionHeader title="Logs" subtitle="Reads os.app.runtime.logs only for admins and redacts token-like values." />
				{!isAdmin ? (
					<MissingDataNotice>Runtime logs hidden: admin role required.</MissingDataNotice>
				) : runtime.loading ? (
					<div style={{ fontSize: 12, color: textMuted }}>Loading runtime logs…</div>
				) : runtime.logsWired && runtime.snapshot?.logs && runtime.snapshot.logs.length > 0 ? (
					<>
						<MetadataRow label="Source">{runtime.snapshot.logsSource ?? 'os.app.runtime.logs'}</MetadataRow>
						<MetadataRow label="Lines">{runtime.snapshot.logs.length}</MetadataRow>
						<div className="overflow-auto rounded border" style={{ maxHeight: 260, marginTop: 10, padding: 8, borderColor: borderDefault, background: surfaceDefault, fontSize: 11, color: textMuted, fontFamily: "'Geist Mono', monospace" }}>
							{runtime.snapshot.logs.slice(-40).map((line, i) => <div key={i} className="whitespace-pre-wrap break-all">{line}</div>)}
						</div>
					</>
				) : (
					<MissingDataNotice>
						Runtime logs not wired: os.app.runtime.logs unavailable for this installed app.
					</MissingDataNotice>
				)}
			</SettingsCard>
		</div>
	);
}

function UsageCostTab({ appId, manifest, installedAppId }: { appId: string; manifest?: AppManifestInfo; installedAppId?: string }) {
	const isV2 = isRuntimeV2Image(appId, manifest);
	const runtime = useRuntimeV2Snapshot(appId, installedAppId, isV2, false);
	const cost = runtime.snapshot?.cost;
	const maxSeriesValue = Math.max(...(cost?.series ?? []).map((item) => item.value), 0);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<SettingsCard>
				<SettingsSectionHeader title="Usage-Cost" subtitle="Only renders real or explicitly derived values from the runtime snapshot." />
				{!isV2 ? (
					<MissingDataNotice>
						Usage/cost data source not wired: legacy service registry has no os.app.runtime.get cost field.
					</MissingDataNotice>
				) : runtime.loading ? (
					<div style={{ fontSize: 12, color: textMuted }}>Loading usage and cost source…</div>
				) : cost ? (
					<>
						<MetadataRow label="Source">{cost.source ?? 'not wired'}</MetadataRow>
						<MetadataRow label="Estimated hourly">{formatMoney(cost.estimatedHourly, cost.currency)}</MetadataRow>
						<MetadataRow label="Estimated daily">{formatMoney(cost.estimatedDaily, cost.currency)}</MetadataRow>
						{cost.series && cost.series.length > 0 ? (
							<div className="k-bars" aria-label="Usage cost series" style={{ marginTop: 12 }}>
								{cost.series.map((item) => (
									<span
										key={item.label}
										className="k-bar"
										title={`${item.label}: ${item.value}`}
										style={{ height: `${maxSeriesValue > 0 ? Math.max(4, Math.round((item.value / maxSeriesValue) * 100)) : 4}%` }}
									/>
								))}
							</div>
						) : (
							<MissingDataNotice>
								Usage/cost chart data source not wired: os.app.runtime.get cost.series missing.
							</MissingDataNotice>
						)}
						{cost.notes?.map((note) => (
							<div key={note} style={{ fontSize: 12, color: textMuted, marginTop: 6 }}>{note}</div>
						))}
					</>
				) : (
					<MissingDataNotice>
						Usage/cost data source not wired: os.app.runtime.get returned no cost field.
					</MissingDataNotice>
				)}
			</SettingsCard>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Control Tab
// ---------------------------------------------------------------------------

function ControlTab({ appId, manifest }: { appId: string; manifest?: AppManifestInfo }) {
	const isV2 = isRuntimeV2Image(appId, manifest);
	const { connected, request, orgId } = useNats();
	const [services, setServices] = useState<ServiceInfo[]>([]);

	useEffect(() => {
		if (isV2) {
			setServices([]);
			return;
		}
		if (!connected) return;
		request(`khal.${orgId}.services.list`, {})
			.then((reply) => {
				const all = (reply as { services?: ServiceInfo[] }).services ?? [];
				const prefix = manifest?.natsPrefix;
				const filtered = prefix
					? all.filter((s) => s.name.includes(prefix) || s.source?.includes(appId))
					: all.filter((s) => s.source?.includes(appId));
				setServices(filtered.length > 0 ? filtered : all);
			})
			.catch(() => {});
	}, [connected, isV2, request, orgId, appId, manifest?.natsPrefix]);

	const hasServices = services.length > 0;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Runtime actions */}
			<SettingsCard>
				<SettingsSectionHeader
					title="Runtime actions"
					subtitle="Destructive operations are disabled during QA unless explicitly confirmed by an owner."
				/>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					<button
						type="button"
						disabled
						className="rounded-lg px-4 py-2.5 text-[12px] font-medium"
						style={{
							background: controlsBg,
							color: textMuted,
							border: `1px solid ${borderDefault}`,
							opacity: 0.5,
							cursor: 'not-allowed',
							textAlign: 'left',
						}}
					>
						Restart runtime — disabled (confirmation required)
					</button>
					<button
						type="button"
						disabled
						className="rounded-lg px-4 py-2.5 text-[12px] font-medium"
						style={{
							background: controlsBg,
							color: textMuted,
							border: `1px solid ${borderDefault}`,
							opacity: 0.5,
							cursor: 'not-allowed',
							textAlign: 'left',
						}}
					>
						Stop runtime — disabled (confirmation required)
					</button>
					<button
						type="button"
						className="rounded-lg px-4 py-2.5 text-[12px] font-medium"
						style={{
							background: controlsBg,
							color: textPrimary,
							border: `1px solid ${borderDefault}`,
							cursor: 'pointer',
							textAlign: 'left',
						}}
						onClick={() => {
							navigator.clipboard.writeText(appId);
						}}
					>
						Copy app id — {appId}
					</button>
				</div>
			</SettingsCard>

			{/* Deployment facts */}
			<SettingsCard>
				<SettingsSectionHeader title="Deployment facts" />
				<MetadataRow label="App id" mono>{appId}</MetadataRow>
				<MetadataRow label="Runtime">{runtimeLabel(manifest)}</MetadataRow>
				{hasServices &&
					services.map((svc) => (
						<div key={svc.name} style={{ marginTop: 8 }}>
							<div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>{svc.name}</div>
							{svc.pid && <MetadataRow label="PID" mono>{svc.pid}</MetadataRow>}
							{svc.ports?.length > 0 && <MetadataRow label="Ports" mono>{svc.ports.join(', ')}</MetadataRow>}
							<MetadataRow label="Restart policy">{svc.restartPolicy || '—'}</MetadataRow>
						</div>
					))}
				{!hasServices && (
					<div style={{ fontSize: 12, color: textMuted }}>
						{isV2 ? 'Runtime v2 control subjects are not wired in this gear panel yet.' : 'No service runtime detected.'}
					</div>
				)}
			</SettingsCard>

			{/* Safety note */}
			<SettingsCard>
				<div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
					<span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
					<div>
						<div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, marginBottom: 4 }}>
							Safety note
						</div>
						<div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
							Destructive operations are disabled during QA unless explicitly confirmed by an owner.
							This protects running instances from accidental stops and restarts during testing.
						</div>
					</div>
				</div>
			</SettingsCard>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Log Viewer
// ---------------------------------------------------------------------------

function LogViewer({ serviceName, isAdmin }: { serviceName: string; isAdmin: boolean }) {
	const { connected, request, subscribe, orgId } = useNats();
	const [logLines, setLogLines] = useState<string[]>([]);
	const [expanded, setExpanded] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isAdmin) {
			setLogLines([]);
			return;
		}
		if (!connected || !serviceName) return;
		setLogLines([]);

		request(`khal.${orgId}.services.logs.${serviceName}.history`, { lines: 80 })
			.then((reply) => setLogLines(sanitizeLogLines((reply as { lines?: string[] }).lines ?? [])))
			.catch(() => {});

		const unsub = subscribe(`khal.*.services.logs.${serviceName}`, (data) => {
			const d = data as { stream?: string; line?: string };
			setLogLines((prev) => sanitizeLogLines([...prev, `[${d.stream}] ${d.line}`]));
		});
		return unsub;
	}, [connected, isAdmin, serviceName, request, subscribe, orgId]);

	useEffect(() => {
		if (scrollRef.current && expanded) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, [logLines, expanded]);

	return (
		<div>
			{!isAdmin ? (
				<div style={{ fontSize: 11, color: textMuted, marginTop: 8 }}>Service logs hidden: admin role required.</div>
			) : (
				<>
					<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				style={{
					fontSize: 11,
					color: COPPER_TEXT,
					background: 'none',
					border: 'none',
					cursor: 'pointer',
					padding: 0,
				}}
			>
				{expanded ? '▾ Hide' : '▸ Show'} recent service logs
			</button>
			{expanded && (
				<div
					ref={scrollRef}
					className="overflow-auto rounded border"
					style={{
						height: 200,
						fontSize: 11,
						lineHeight: '18px',
						padding: '8px 10px',
						marginTop: 8,
						background: surfaceDefault,
						borderColor: borderDefault,
						color: textMuted,
						fontFamily: "'Geist Mono', monospace",
					}}
				>
					{logLines.length === 0 ? (
						<div style={{ color: textMuted }}>No log output yet.</div>
					) : (
						logLines.map((line, i) => (
							<div key={i} className="whitespace-pre-wrap break-all">
								{line}
							</div>
						))
					)}
				</div>
			)}
				</>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

interface TabDef {
	id: AppSettingsTab;
	label: string;
	subtitle: string;
}

const V2_TAB_DEFS: TabDef[] = [
	{ id: 'about', label: 'About', subtitle: 'Manifest, version, permissions' },
	{ id: 'runtime', label: 'Status', subtitle: 'Runtime, deployment, image, health' },
	{ id: 'providers', label: 'Providers', subtitle: 'App BFF readiness checks' },
	{ id: 'environment', label: 'Environment', subtitle: 'Connectors and masked secrets' },
	{ id: 'logs', label: 'Logs', subtitle: 'Redacted runtime log tail' },
	{ id: 'control', label: 'Control', subtitle: 'Safe operations' },
	{ id: 'usage', label: 'Usage-Cost', subtitle: 'Runtime resources and spend source' },
];

const LEGACY_TAB_DEFS: TabDef[] = [
	{ id: 'about', label: 'About', subtitle: 'Manifest, version, permissions' },
	{ id: 'runtime', label: 'Runtime', subtitle: 'Service runtime readiness' },
	{ id: 'environment', label: 'Environment', subtitle: 'Connectors and masked secrets' },
	{ id: 'control', label: 'Control', subtitle: 'Safe operations' },
];

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export function AppSettingsPanel({
	windowId: _windowId,
	appId,
	manifest,
	onClose,
	initialTab,
	envClient,
	installedAppId,
	isAdmin = false,
}: AppSettingsPanelProps) {
	const [visible, setVisible] = useState(false);
	const isV2 = isRuntimeV2Image(appId, manifest);
	const hasRuntimeTab = appHasServices(manifest);
	const hasEnv = appHasEnvTab(manifest, envClient, isAdmin);

	// Build available tabs
	const availableTabDefs: TabDef[] = [];
	const tabSource = isV2 ? V2_TAB_DEFS : LEGACY_TAB_DEFS;
	for (const def of tabSource) {
		if (def.id === 'runtime' && !hasRuntimeTab) continue;
		if (def.id === 'environment' && !isV2 && !hasEnv) continue;
		availableTabDefs.push(def);
	}
	// Always have at least About
	if (availableTabDefs.length === 0) {
		availableTabDefs.push(V2_TAB_DEFS[0]!);
	}

	// Resolve initial tab — map legacy service/status to runtime.
	const resolveTab = (t: AppSettingsTab | 'service' | 'integrations'): AppSettingsTab => {
		if (t === 'service' || t === 'status') return 'runtime';
		if (t === 'integrations') return 'about';
		return t;
	};
	const resolvedInitial = resolveTab((initialTab ?? 'about') as AppSettingsTab | 'service' | 'integrations');
	const validInitial = availableTabDefs.some((d) => d.id === resolvedInitial)
		? resolvedInitial
		: availableTabDefs[0]!.id;

	const [activeTab, setActiveTab] = useState<AppSettingsTab>(validInitial);

	// React to a parent that changes `initialTab` after mount
	useEffect(() => {
		if (initialTab) {
			const resolved = resolveTab(initialTab);
			if (availableTabDefs.some((d) => d.id === resolved) && resolved !== activeTab) {
				setActiveTab(resolved);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialTab]);

	// Fade-in animation
	useEffect(() => {
		requestAnimationFrame(() => setVisible(true));
	}, []);

	// Settings is a temporary desktop-level overlay, not a replacement for the
	// app. Keep keyboard recovery obvious even if the user misses the close icon.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	const appLabel = manifest?.label ?? appId;

	const statusVariant: 'running' | 'needs-config' | 'unavailable' =
		isV2 ? 'needs-config' : manifest?.natsPrefix ? 'needs-config' : 'unavailable';
	const statusLabel = isV2
		? 'Runtime data pending'
		: manifest?.natsPrefix
			? 'Service runtime'
			: hasEnv
				? 'Configuration available'
				: 'Runtime optional';

	return (
		<>
			{/* Backdrop */}
			<div
				style={{
					position: 'fixed',
					inset: 0,
					zIndex: 9299,
					background: 'rgba(0,0,0,0.4)',
					opacity: visible ? 1 : 0,
					transition: 'opacity 160ms ease-out',
				}}
				onClick={onClose}
				data-no-drag
			/>

			{/* Dialog */}
			<div
				role="dialog"
				aria-label={`${appLabel} settings`}
				className="fixed left-1/2 top-1/2 z-[9300] flex flex-col overflow-hidden shadow-2xl"
				style={{
					width: 'min(920px, calc(100vw - 48px))',
					height: 'min(720px, calc(100vh - var(--khal-taskbar-height, 44px) - 56px))',
					minWidth: 'min(760px, calc(100vw - 48px))',
					minHeight: 'min(560px, calc(100vh - var(--khal-taskbar-height, 44px) - 56px))',
					transform: 'translate(-50%, -50%)',
					borderRadius: 16,
					border: `1px solid ${borderDefault}`,
					background: surfaceDefault,
					opacity: visible ? 1 : 0,
					transition: 'opacity 200ms ease-out',
				}}
				onPointerDown={(e) => e.stopPropagation()}
				data-no-drag
			>
				{/* Header */}
				<div
					className="flex shrink-0 items-center justify-between px-5"
					style={{
						height: 52,
						borderBottom: `1px solid ${borderDefault}`,
					}}
				>
					<div className="flex items-center gap-3">
						<button
							type="button"
							className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors"
							style={{
								color: COPPER_TEXT,
								background: COPPER_SOFT,
								border: `1px solid ${borderDefault}`,
							}}
							onClick={onClose}
							aria-label={`Back to ${appLabel}`}
						>
							<ArrowLeft size={14} aria-hidden="true" />
							Back to app
						</button>
						<div>
							<div style={{ fontSize: 15, fontWeight: 600, color: textPrimary, lineHeight: 1.3 }}>
								{appLabel} settings
							</div>
							<div style={{ fontSize: 11, color: textMuted, lineHeight: 1.3 }}>
								{runtimeLabel(manifest)}{' · '}
								{manifest?.permission ?? 'read-only'}
								{manifest?.minRole && ` · min ${manifest.minRole}`}
							</div>
						</div>
						<StatusBadge variant={statusVariant}>
							{statusLabel}
						</StatusBadge>
					</div>
					<button
						type="button"
						className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors"
						style={{ color: textMuted }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLElement).style.background = controlsBg;
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLElement).style.background = 'transparent';
						}}
						onClick={onClose}
						aria-label="Close settings"
					>
						<X size={14} />
					</button>
				</div>

				{/* Body: left rail + content */}
				<div className="flex flex-1 overflow-hidden">
					{/* Tab rail */}
					<nav
						className="k-rail flex shrink-0 flex-col gap-0.5 overflow-y-auto px-3 py-4"
						style={{
							width: 200,
							borderRight: `1px solid ${borderDefault}`,
						}}
					>
						{availableTabDefs.map((def) => {
							const isActive = activeTab === def.id;
							return (
								<button
									key={def.id}
									type="button"
									className="w-full rounded-lg px-3 py-2.5 text-left transition-colors"
									style={{
										background: isActive ? COPPER_SOFT : 'transparent',
										borderLeft: isActive ? `2px solid ${COPPER}` : '2px solid transparent',
									}}
									onClick={() => setActiveTab(def.id)}
								>
									<div
										style={{
											fontSize: 13,
											fontWeight: isActive ? 600 : 500,
											color: isActive ? COPPER_TEXT : textMuted,
										}}
									>
										{def.label}
									</div>
									<div style={{ fontSize: 11, color: textMuted, marginTop: 1 }}>
										{def.subtitle}
									</div>
								</button>
							);
						})}
					</nav>

					{/* Content area */}
					<main className="k-content flex-1 overflow-y-auto px-5 py-4">
						{activeTab === 'about' && <AboutTab appId={appId} manifest={manifest} />}
						{activeTab === 'runtime' && <RuntimeTab appId={appId} manifest={manifest} installedAppId={installedAppId} isAdmin={isAdmin} />}
						{activeTab === 'providers' && <ProvidersTab appId={appId} manifest={manifest} />}
						{activeTab === 'environment' && (
							envClient ? (
								<AppEnvPanel
									installedAppId={installedAppId ?? appId}
									schema={manifest?.env ?? []}
									client={envClient}
									isAdmin={isAdmin}
									grouping={resolveEnvGrouping}
								/>
							) : (
								<EnvironmentUnavailableTab />
							)
						)}
						{activeTab === 'logs' && <LogsTab appId={appId} manifest={manifest} installedAppId={installedAppId} isAdmin={isAdmin} />}
						{activeTab === 'control' && <ControlTab appId={appId} manifest={manifest} />}
						{activeTab === 'usage' && <UsageCostTab appId={appId} manifest={manifest} installedAppId={installedAppId} />}
					</main>
				</div>
			</div>
		</>
	);
}
